import type { GridColumn, GridSetFilterModel } from '../types'

/**
 * A set filter is active only when it holds at least one value. An empty
 * selection means "no filter", not "match nothing" — that is how ag-grid's set
 * filter behaved, and pages rely on it to tell an untouched column from one the
 * user has deliberately narrowed.
 */
export const isSetFilterActive = (model: unknown): model is GridSetFilterModel =>
  Boolean(
    model &&
      typeof model === 'object' &&
      Array.isArray((model as GridSetFilterModel).values) &&
      (model as GridSetFilterModel).values.length > 0,
  )

/** Normalises a selection into a model, or `null` when nothing is selected. */
export const toSetFilterModel = (values: unknown[]): GridSetFilterModel | null =>
  values.length > 0 ? { values: [...values] } : null

/**
 * Client-side pass for `'set'` columns. Custom filter components are not applied
 * here: they belong to the page (the Controllers Purpose filter, for one, is
 * resolved by the server), so the grid only carries their model.
 */
export const applySetFilters = <T,>(
  rows: T[],
  columns: GridColumn<T>[],
  filterModel: Record<string, unknown>,
): T[] => {
  const active = columns.filter(
    (column) => column.filter === 'set' && isSetFilterActive(filterModel[column.field]),
  )
  if (active.length === 0) return rows

  const selections = active.map((column) => ({
    column,
    values: new Set(
      (filterModel[column.field] as GridSetFilterModel).values.map((value) =>
        String(value),
      ),
    ),
  }))

  return rows.filter((row) =>
    selections.every(({ column, values }) => {
      const value = column.valueGetter
        ? column.valueGetter(row)
        : (row as Record<string, unknown>)[column.field]
      return values.has(String(value ?? ''))
    }),
  )
}
